import { useState } from 'react';
import { useStore } from '../../context/StoreContext';
import { useToast } from '../../context/ToastContext';
import { ShieldCheck, ArrowRight, Heart } from 'lucide-react';

export function Footer({ onNavigate }: { onNavigate: (path: string) => void }) {
  const { settings } = useStore();
  const { showToast } = useToast();
  const [newsletterEmail, setNewsletterEmail] = useState('');

  const handleNewsletterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newsletterEmail.trim()) {
      showToast('success', 'Inscrição confirmada', 'Você receberá nossos boletins editoriais e lançamentos exclusivos.');
      setNewsletterEmail('');
    }
  };

  return (
    <footer className="bg-surface border-t border-border-subtle mt-auto text-ink">
      {/* Editorial Newsletter Strip */}
      <div className="border-b border-border-subtle bg-background-alt py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="max-w-md">
              <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-primary">Boletim do Ateliê</span>
              <h3 className="text-xl sm:text-2xl font-serif font-normal mt-1 text-ink">
                Receba histórias de ofício e edições limitadas
              </h3>
              <p className="text-xs text-ink-muted mt-1 leading-relaxed">
                Compartilhamos ensaios visuais sobre o processo cerâmico, lançamentos sazonais e convites para eventos de ateliê.
              </p>
            </div>

            <form onSubmit={handleNewsletterSubmit} className="w-full md:w-auto flex flex-col sm:flex-row gap-2 max-w-md">
              <input
                type="email"
                required
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                placeholder="Seu melhor e-mail"
                className="bg-surface border border-border-subtle px-4 py-3 text-xs rounded-sm focus:outline-none focus:border-primary min-w-[260px]"
              />
              <button
                type="submit"
                className="bg-ink text-ink-inverted text-xs uppercase tracking-wider font-medium px-5 py-3 rounded-sm hover:bg-primary transition-colors flex items-center justify-center gap-1.5 shrink-0"
              >
                Inscrever-se
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Main Footer Links */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">
          {/* Brand Col */}
          <div className="lg:col-span-2 space-y-4">
            <span className="font-serif text-2xl font-medium tracking-tight text-ink block">
              {settings.brand_name || 'Aura Ateliê'}
            </span>
            <p className="text-xs text-ink-muted leading-relaxed max-w-sm">
              {settings.brand_description ||
                'Estúdio autoral brasileiro dedicado à investigação da cerâmica de alta temperatura, têxteis orgânicos e criações contemporâneas para o espaço do viver.'}
            </p>
            <div className="pt-2 text-xs text-ink-muted space-y-1">
              <p><strong className="text-ink font-medium">Atendimento:</strong> {settings.contact_email}</p>
              <p><strong className="text-ink font-medium">WhatsApp / Concierge:</strong> {settings.contact_phone}</p>
              <p><strong className="text-ink font-medium">Ateliê Físico:</strong> {settings.address_line}</p>
              <p><strong className="text-ink font-medium">CNPJ:</strong> {settings.cnpj}</p>
            </div>
          </div>

          {/* Links: Catálogo */}
          <div>
            <h4 className="text-xs uppercase tracking-widest font-semibold text-ink mb-4">Coleções</h4>
            <ul className="space-y-2.5 text-xs text-ink-muted">
              <li>
                <button onClick={() => onNavigate('/catalogo')} className="hover:text-primary transition-colors">
                  Todas as Peças
                </button>
              </li>
              <li>
                <button onClick={() => onNavigate('/categoria/ceramica-artesanal')} className="hover:text-primary transition-colors">
                  Cerâmica Autoral
                </button>
              </li>
              <li>
                <button onClick={() => onNavigate('/categoria/linha-mesa-e-decoracao')} className="hover:text-primary transition-colors">
                  Mesa & Têxteis em Linho
                </button>
              </li>
              <li>
                <button onClick={() => onNavigate('/categoria/acervo-digital-e-arte')} className="hover:text-primary transition-colors">
                  Acervo Digital & Presets
                </button>
              </li>
              <li>
                <button onClick={() => onNavigate('/categoria/consultorias-e-encomendas')} className="hover:text-primary transition-colors">
                  Consultorias & Encomendas
                </button>
              </li>
            </ul>
          </div>

          {/* Links: Institucional */}
          <div>
            <h4 className="text-xs uppercase tracking-widest font-semibold text-ink mb-4">Ateliê & Ajuda</h4>
            <ul className="space-y-2.5 text-xs text-ink-muted">
              <li>
                <button onClick={() => onNavigate('/politicas/sobre')} className="hover:text-primary transition-colors">
                  Sobre a Nossa História
                </button>
              </li>
              <li>
                <button onClick={() => onNavigate('/politicas/envio')} className="hover:text-primary transition-colors">
                  Prazos & Envio
                </button>
              </li>
              <li>
                <button onClick={() => onNavigate('/politicas/trocas')} className="hover:text-primary transition-colors">
                  Trocas & Devoluções
                </button>
              </li>
              <li>
                <button onClick={() => onNavigate('/politicas/contato')} className="hover:text-primary transition-colors">
                  Fale Conosco
                </button>
              </li>
              <li>
                <button onClick={() => onNavigate('/conta')} className="hover:text-primary transition-colors">
                  Acompanhar Pedido
                </button>
              </li>
            </ul>
          </div>

          {/* Links: Segurança e Compliance */}
          <div>
            <h4 className="text-xs uppercase tracking-widest font-semibold text-ink mb-4">Segurança & Legal</h4>
            <ul className="space-y-2.5 text-xs text-ink-muted">
              <li>
                <button onClick={() => onNavigate('/politicas/privacidade')} className="hover:text-primary transition-colors">
                  Privacidade & LGPD
                </button>
              </li>
              <li>
                <button onClick={() => onNavigate('/politicas/termos')} className="hover:text-primary transition-colors">
                  Termos de Serviço
                </button>
              </li>
              <li className="pt-2 flex items-center gap-2 text-olive font-medium">
                <ShieldCheck className="w-4 h-4" />
                <span>Dados Criptografados (AES-256)</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-border-subtle flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-ink-muted">
          <p>© {new Date().getFullYear()} Aura Ateliê Brasil Ltda. Todos os direitos reservados.</p>
          <div className="flex items-center gap-2">
            <span>Criado com rigor artesanal em São Paulo</span>
            <Heart className="w-3.5 h-3.5 text-primary fill-primary" />
          </div>
        </div>
      </div>
    </footer>
  );
}
